import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { MonthlyTrend } from '../../types';
import { useChartColors } from './useChartColors';

interface MonthlyTrendChartProps {
  data: MonthlyTrend[];
  formatCurrency: (amount: number) => string;
  noCard?: boolean;
}

export default function MonthlyTrendChart({ data, formatCurrency, noCard = false }: MonthlyTrendChartProps) {
  const { t } = useTranslation('dashboard');
  const c = useChartColors();
  if (data.length === 0) {
    const emptyContent = (
      <>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('monthlyTrendDetails.sixMonthTrend')}</h3>
        <div className="flex flex-col items-center justify-center h-48 text-gray-500">
          <TrendingUp className="w-12 h-12 mb-2 opacity-50" />
          <p className="text-sm">{t('monthlyTrendDetails.notEnoughData')}</p>
        </div>
      </>
    );
    if (noCard) return emptyContent;
    return <div className="card h-full">{emptyContent}</div>;
  }

  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number; name: string; color: string }>; label?: string }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-100">
          <p className="font-medium text-gray-900 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const chartContent = (
    <>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('monthlyTrendDetails.sixMonthTrend')}</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={c.grid} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 12, fill: c.axisText }}
              tickLine={false}
              axisLine={{ stroke: c.grid }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: c.axisText }}
              tickLine={false}
              axisLine={{ stroke: c.grid }}
              tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              formatter={(value) => <span className="text-sm text-gray-600">{value}</span>}
              wrapperStyle={{ fontSize: '12px' }}
            />
            <Line
              type="monotone"
              dataKey="totalIncome"
              name={t('monthlyTrendDetails.income')}
              stroke={c.credit}
              strokeWidth={2}
              dot={{ fill: c.credit, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="totalExpenses"
              name={t('monthlyTrendDetails.expenses')}
              stroke={c.debit}
              strokeWidth={2}
              dot={{ fill: c.debit, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="balance"
              name={t('monthlyTrendDetails.balance')}
              stroke={c.primary}
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: c.primary, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </>
  );

  if (noCard) return chartContent;
  return <div className="card h-full">{chartContent}</div>;
}
